import Skeleton from '@/common/components/atom/skeleton';
import { useSearchRouter } from '../../hooks/useSearchRouter';
import { useSearch } from '../../hooks/useSearch';
import AreaEmptyState from '../areaEmpty';
import { useSearchRouting } from '../../hooks/useSearchRouting';
import { useSearchStore } from '../../store/search';
import { Fragment } from '@/common/fragment';
import getConfig from 'next/config';
const { publicRuntimeConfig } = getConfig();
import SearchGlobalContextsProvider from '../../../../../.plasmic/plasmic/paziresh_24_search/PlasmicGlobalContextsProvider';
import Button from '@/common/components/atom/button';
import LoadingAtom from '@/common/components/atom/loading';
import Text from '@/common/components/atom/text';
import axios from 'axios';
import { getErrorMessage } from '@/common/utils/errorHandler';
import { getImageUrl } from '@/common/utils/getImageUrl';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { isResultsCodeReady, loadResultsCode } from '../../functions/resultsCode';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { stripCityFromOnlineLandingTitles } from '../../functions/onlineLandingTitles';
// LCP fix (DR-08 §۱۸, docs/dev-requests/dr-08-listing-pagespeed.md): this banner used to be
// `ssr: false`, which meant the server sent nothing for it and its own text could not paint until
// the chunk was downloaded and mounted. RUM (index=p24_web_vitals) showed that on 20% of mobile
// `listing_specialty_city` views this banner's text *is* the LCP element, with a p75
// `elementRenderDelay` of 3.8s — i.e. the largest paint on the page was waiting on a client-only
// component. Nothing in it needs the browser: both of its shapes are derived from `selectedCategory`
// /`isConsult`, which are already resolved server-side, and the substitute-doctor request it makes is
// gated behind `enabled: modalProps.isOpen`. `dynamic()` is kept (without `ssr: false`) so the chunk
// still stays out of the initial JS bundle for TBT — only the *markup* moves to the server.
// No loading placeholder: on a client navigation the banner is held back until the list is revealed
// (see `hold` below), so a placeholder would only appear and then collapse (docs/performance.md §۳.۲۱).
const ConsultBanner = dynamic(() => import('@/modules/search/components/consultBanner'), { loading: () => null });
// «ویزیت آنلاین سازمانی»: signed-in users only, and it renders for one account (corporate/rollout.ts).
const CorporateSearchWelcome = dynamic<{ className?: string }>(() => import('@/modules/corporate/live').then(m => m.CorporateSearchWelcome), { ssr: false });

export const Result = ({ showConsultBanner = false }: { showConsultBanner?: boolean }) => {
  const {
    query: { params, ...query },
  } = useSearchRouter();

  const { result: rawResult, searchCity, isLanding, isLoading, search, responseData, searchConsultResponseData, isError, error, refetch, area } = useSearch();
  // Online visit has no city: on /s/<city>/?turn_type=consult the specialty links must not say «… شهرکرد».
  const result = stripCityFromOnlineLandingTitles(rawResult, isLanding, query.turn_type === 'consult', searchCity?.name);
  const { changeRoute } = useSearchRouting();
  const city = useSearchStore(state => state.city);
  const geoLocation = useSearchStore(state => state.geoLocation);
  const isLogin = useUserInfoStore(state => state.isLogin);

  // On a client navigation from another page the list's code (and the banner's) is not loaded yet.
  // Keep the loading skeleton until it is, instead of dropping to an empty column for a few frames
  // (docs/performance.md §۳.۲۱). Only a mount that actually showed the skeleton waits: an SSR/hydrated
  // render never does, so its markup is unchanged.
  const loadingList = isLoading && !isError && result.length == 0;
  const [codeReady, setCodeReady] = useState(isResultsCodeReady);
  const [awaitingCode, setAwaitingCode] = useState(false);
  if (loadingList && !codeReady && !awaitingCode) setAwaitingCode(true);
  useEffect(() => {
    if (!awaitingCode || codeReady) return;
    let active = true;
    loadResultsCode().finally(() => active && setCodeReady(true));
    return () => {
      active = false;
    };
  }, [awaitingCode, codeReady]);
  const holdForCode = awaitingCode && !codeReady && !isError;
  const showSkeleton = loadingList || holdForCode;
  // The skeleton's shape follows the loading-time guess; keep it while holding so it does not
  // switch from cards to lines under the user once `isLanding` comes from the response.
  const skeletonLine = useRef(isLanding);
  if (loadingList) skeletonLine.current = isLanding;

  const handleNextPage = () => {
    const currentPage = (query?.page as string) ? (query?.page as string) : 1;
    changeRoute({
      query: {
        page: +currentPage + 1,
      },
      scroll: false,
    });
  };

  return (
    <>
      {showConsultBanner && (
        // Held while the skeleton is up: it keeps what it last showed (nothing yet after a client
        // navigation) and takes the new category in the same commit that replaces the skeleton, so
        // growing from the no-category shape never pushes the skeleton down.
        <div className="md:hidden">
          <ConsultBanner hold={showSkeleton} />
        </div>
      )}
      {isError && (
        <div className="flex flex-col justify-center items-center w-full space-y-3 p-5">
          <span className="font-semibold text-center">
            {axios.isAxiosError(error) && error.response?.status === 429
              ? 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً کمی صبر کنید و دوباره تلاش کنید.'
              : getErrorMessage(error) || 'خطا در دریافت نتایج جستجو'}
          </span>
          <Button onClick={() => refetch()}>تلاش مجدد</Button>
        </div>
      )}
      {showSkeleton && (
        <div className="flex flex-col w-full space-y-3">
          <div className="flex flex-col p-5 space-y-4 rounded-lg bg-white">
            <div className="flex items-center space-s-2">
              <LoadingAtom />
              <Text>در حال آماده سازی نتایج جستجو ...</Text>
            </div>
          </div>
          <Loading line={skeletonLine.current} />
        </div>
      )}
      {/* DR-43: an empty area page says what happened and offers the two ways out, not «عبارات کامل‌تری وارد کنید» */}
      {!holdForCode && area && result.length === 0 && !isLoading && !isError && <AreaEmptyState />}
      {!holdForCode && !(area && result.length === 0 && !isError) && (result.length === 0 ? !isLoading : !isError) && (
        <SearchGlobalContextsProvider>
          <>
            {isLogin && <CorporateSearchWelcome className="mb-3" />}
            <Fragment
              name="SearchResults"
              props={{
                searchResultResponse: {
                  ...responseData,
                  search: {
                    ...search,
                    result,
                  },
                },
                searchConsultResponse: {
                  ...searchConsultResponseData,
                },
                nextPageTrigger: handleNextPage,
                imageSrcPrefix: publicRuntimeConfig.CDN_BASE_URL,
                location: {
                  city_name: city?.en_slug,
                  city_id: city?.id,
                  ...city,
                  ...geoLocation,
                },
                paginationLoadingStatus: isLoading,
                overrides: {
                  topSuggestedCard: {
                    avatarSrc: getImageUrl(searchConsultResponseData?.search?.result?.[0]?.image, publicRuntimeConfig.CDN_BASE_URL),
                  },
                },
              }}
            />
          </>
        </SearchGlobalContextsProvider>
      )}
    </>
  );
};

const CardSkleton = () => (
  <Skeleton w="100%" h={'15rem'} rounded="lg" className="bg-white p-4 gap-3 justify-between flex flex-col">
    <div className="flex items-center gap-3">
      <Skeleton w="5rem" h="5rem" rounded="full" className="shrink-0 min-w-[5rem]" />
      <div className="flex min-w-0 flex-col gap-2">
        <Skeleton w="10rem" h="1rem" rounded="full" />
        <Skeleton w="15rem" h="1rem" rounded="full" className="max-w-full" />
        <Skeleton w="2rem" h="0.8rem" rounded="full" />
      </div>
    </div>
    <div className="flex flex-col gap-3">
      <Skeleton w="10rem" h="1rem" rounded="full" />
      <Skeleton w="5rem" h="1rem" rounded="full" />
    </div>
    <div className="flex gap-3">
      <Skeleton w="100%" h="3rem" rounded="lg" />
      <Skeleton w="100%" h="3rem" rounded="lg" />
    </div>
  </Skeleton>
);

const Loading = ({ line = false }: { line?: boolean }) => {
  if (!line)
    return (
      <>
        <CardSkleton />
        <CardSkleton />
        <CardSkleton />
      </>
    );

  return (
    <>
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
      <Skeleton w="100%" h={'3.6rem'} rounded="lg" />
    </>
  );
};

export default Result;
